import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { FaBookOpen, FaEdit, FaSave, FaTimes } from "react-icons/fa";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

//Created By Cursor
export default function MdlEditSubjectMasterList({
  open,
  subject_name = "",
  subject_id = "",
  session_id = "",
  onClose,
  actionClose,
}) {
  const close = onClose || actionClose;
  const [subjectName, setSubjectName] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSubjectName(subject_name || "");
    setError("");
  }, [open, subject_name, subject_id]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !saving && typeof close === "function") {
        close();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close, saving]);

  //Created By Cursor
  const handleSave = async () => {
    const name = subjectName.trim();
    if (!name) {
      setError("Please enter a subject name");
      return;
    }
    if (!subject_id) {
      myToast.notify_warning("Subject id is missing.");
      return;
    }
    if (!session_id) {
      myToast.notify_warning("Session is missing.");
      return;
    }
    setError("");
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      myToast.notify_warning("School code missing.");
      return;
    }

    setSaving(true);
    try {
      const res = await axios.get(`${API_BASE()}updateSubjectName.php`, {
        params: {
          school_code: schoolCode,
          subject_id,
          subject_name: name,
          //Created By Cursor — session-wise update only
          session_id,
        },
      });
      if (res.data?.api_status === "OK") {
        myToast.notify_success("Subject updated.");
        if (typeof close === "function") close();
      } else {
        myToast.notify_warning(
          res.data?.api_remark || "Failed to update subject."
        );
      }
    } catch (err) {
      console.error("[MdlEditSubjectMasterList]", err);
      myToast.notify_error("Failed to update subject.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const modal = (
    <div
      className="sl-mdl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sl-mdl-edit-title"
    >
      <button
        type="button"
        className="sl-mdl__backdrop"
        aria-label="Close"
        disabled={saving}
        onClick={close}
      />
      <div className="sl-mdl__panel">
        <header
          className="sl-mdl__hero"
          style={{
            background: `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`,
          }}
        >
          <div className="sl-mdl__hero-main">
            <div className="sl-mdl__hero-icon" aria-hidden>
              <FaEdit />
            </div>
            <div className="min-w-0">
              <p className="sl-mdl__eyebrow">Edit master subject</p>
              <h2 id="sl-mdl-edit-title" className="sl-mdl__title">
                {subject_name || "Subject"}
              </h2>
              {subject_id ? (
                <div className="sl-mdl__meta">
                  <span>
                    <FaBookOpen aria-hidden /> {subject_id}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            className="sl-mdl__close"
            onClick={close}
            disabled={saving}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </header>

        <div className="sl-mdl__body">
          <label className="sl-mdl__field">
            <span>Subject name</span>
            <div className="sl-mdl__input-wrap">
              <FaBookOpen aria-hidden />
              <input
                type="text"
                value={subjectName}
                onChange={(e) => {
                  setSubjectName(e.target.value);
                  if (error) setError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSave();
                }}
                placeholder="Enter subject name"
                autoFocus
                disabled={saving}
              />
            </div>
            {error ? <em className="sl-mdl__error">{error}</em> : null}
          </label>
        </div>

        <footer className="sl-mdl__footer">
          <button
            type="button"
            className="sl-mdl__btn sl-mdl__btn--ghost"
            disabled={saving}
            onClick={close}
          >
            Cancel
          </button>
          <button
            type="button"
            className="sl-mdl__btn sl-mdl__btn--primary"
            disabled={saving}
            onClick={handleSave}
          >
            <FaSave />
            <span>{saving ? "Saving…" : "Save changes"}</span>
          </button>
        </footer>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
